import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { request } from '../utils/api-client';
import { AppRoutes } from './routes';
import { getTenantApiPath } from './routing';
import { withTelegramAuth } from './telegramAuth';
import { useTelegramAppTheme } from './hooks/useTelegramAppTheme';
import { StaffRolesProvider, type StaffRolesMap } from './context/StaffRolesContext';
import { AuthProvider, useMiniAppAuth } from './context/AuthContext';
import AuthRequiredScreen from './components/AuthRequiredScreen';
import LoadingSpinner from './components/LoadingSpinner';
import { ThemeProvider } from '@ui-kit';
import { LanguageProvider } from '@miniapp-core/context/LanguageContext';
import MiniappHeaderActions from './components/MiniappHeaderActions';
import { MiniappHeaderNavigation } from '@miniapp-core/components';
import { TenantTelegramProvider, type TenantTelegramInfo } from '@frontend-core/telegram';

declare const __UI_KIT_SHOWCASE__: boolean;

interface ServicesResponse {
  data?: unknown;
  staffRoles?: unknown;
}

const loadTenantTelegram = (): Promise<TenantTelegramInfo> =>
  request<{ data: TenantTelegramInfo }>(
    getTenantApiPath('/public/telegram'),
    withTelegramAuth(),
  ).then((response) => response.data);

function normalizeServices(input: unknown): string[] {
  if (!Array.isArray(input)) {
    return [];
  }

  const normalized = input
    .filter((value): value is string => typeof value === 'string')
    .map((service) => service.trim())
    .filter((service) => service.length > 0);

  return Array.from(new Set(normalized));
}

function normalizeStaffRoles(input: unknown): StaffRolesMap {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return {};
  }

  const rolesByService = input as Record<string, unknown>;
  const normalized: StaffRolesMap = {};

  for (const [service, rawRoleMap] of Object.entries(rolesByService)) {
    if (!rawRoleMap || typeof rawRoleMap !== 'object' || Array.isArray(rawRoleMap)) {
      continue;
    }

    const roleEntries = Object.entries(rawRoleMap as Record<string, unknown>)
      .filter(([, value]) => typeof value === 'boolean')
      .map(([role, value]) => [role, value] as const);

    if (roleEntries.length === 0) {
      continue;
    }

    normalized[service] = Object.fromEntries(roleEntries) as Record<string, boolean>;
  }

  return normalized;
}

function AuthenticatedShell() {
  const { t } = useTranslation('shell');
  const [activeServices, setActiveServices] = useState<string[]>([]);
  const [staffRoles, setStaffRoles] = useState<StaffRolesMap>({});
  const [servicesLoading, setServicesLoading] = useState(true);
  const [servicesErrorKey, setServicesErrorKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadServices = async () => {
      setServicesLoading(true);
      setServicesErrorKey(null);

      try {
        const response = await request<ServicesResponse>(
          getTenantApiPath('/services'),
          withTelegramAuth(),
        );

        if (!cancelled) {
          setActiveServices(normalizeServices(response.data));
          setStaffRoles(normalizeStaffRoles(response.staffRoles));
        }
      } catch {
        if (!cancelled) {
          setServicesErrorKey('errors.loadServices');
          setActiveServices([]);
          setStaffRoles({});
        }
      } finally {
        if (!cancelled) {
          setServicesLoading(false);
        }
      }
    };

    void loadServices();

    return () => {
      cancelled = true;
    };
  }, []);

  const servicesError = servicesErrorKey ? t(servicesErrorKey) : null;

  return (
    <StaffRolesProvider roles={staffRoles}>
      <MiniappHeaderNavigation
        backLabel={t('navigation.back')}
        actions={<MiniappHeaderActions />}
        rootScreenPaths={['/restaurants/welcome', '/restaurants/manager/claim']}
      >
        <AppRoutes
          activeServices={activeServices}
          staffRoles={staffRoles}
          servicesLoading={servicesLoading}
          servicesError={servicesError}
        />
      </MiniappHeaderNavigation>
    </StaffRolesProvider>
  );
}

function AuthGatedShell() {
  const auth = useMiniAppAuth();

  if (__UI_KIT_SHOWCASE__) {
    return <AuthenticatedShell />;
  }

  if (auth.status === 'loading') {
    return <LoadingSpinner />;
  }

  if (auth.status === 'unauthenticated') {
    return <AuthRequiredScreen />;
  }

  return <AuthenticatedShell />;
}

export default function App() {
  const telegramColorScheme = useTelegramAppTheme();

  return (
    <ThemeProvider {...(telegramColorScheme ? { systemOverride: telegramColorScheme } : {})}>
      <AuthProvider>
        <TenantTelegramProvider load={loadTenantTelegram}>
          <LanguageProvider>
            <AuthGatedShell />
          </LanguageProvider>
        </TenantTelegramProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
